import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Plus, RotateCcw, Save, Trash2 } from "lucide-react";
import { menu as defaultMenu } from "../lib/data";
import { fetchManagedMenu, saveManagedMenu } from "../lib/menuApi";
import { cleanMenuForSave, createMenuId, withMenuIds } from "../lib/menuEditor";
import { CategoryList } from "../components/MenuAdmin/CategoryList";
import { ItemEditor } from "../components/MenuAdmin/ItemEditor";

const statusClasses = {
  loading: "bg-[#f1c98a]/25 text-[#2c3528] border-[#f1c98a]/50",
  info: "bg-[#5b7053]/10 text-[#2c3528] border-[#5b7053]/25",
  success: "bg-[#5b7053]/15 text-[#2c3528] border-[#5b7053]/35",
  error: "bg-[#8f3d2f]/10 text-[#8f3d2f] border-[#8f3d2f]/30",
};

export default function MenuAdmin() {
  const [categories, setCategories] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: "loading", text: "Loading menu manager…" });

  useEffect(() => {
    let mounted = true;

    const loadMenu = async () => {
      try {
        const data = await fetchManagedMenu();
        const source = data.categories?.length ? data.categories : defaultMenu;
        const nextCategories = withMenuIds(source);
        if (!mounted) return;
        setCategories(nextCategories);
        setActiveId(nextCategories[0]?.id || "");
        setStatus({
          type: "info",
          text: data.categories?.length
            ? "Saved menu loaded. Edit sections and press Save changes to publish."
            : "Current website menu loaded. Press Save changes to enable managed updates.",
        });
      } catch (error) {
        const nextCategories = withMenuIds(defaultMenu);
        if (!mounted) return;
        setCategories(nextCategories);
        setActiveId(nextCategories[0]?.id || "");
        setStatus({ type: "error", text: error.message || "Could not load saved menu." });
      }
    };

    loadMenu();
    return () => {
      mounted = false;
    };
  }, []);

  const activeCategory = useMemo(
    () => categories.find((category) => category.id === activeId),
    [activeId, categories],
  );

  const updateActiveCategory = (field, value) => {
    setCategories((current) =>
      current.map((category) =>
        category.id === activeId ? { ...category, [field]: value } : category,
      ),
    );
  };

  const addCategory = () => {
    const section = { id: createMenuId("cat"), name: "New menu section", blurb: "", items: [] };
    setCategories((current) => [...current, section]);
    setActiveId(section.id);
    setStatus({ type: "info", text: "New section added. Add items, then save changes." });
  };

  const deleteActiveCategory = () => {
    if (!activeCategory || !window.confirm(`Delete ${activeCategory.name}?`)) return;
    setCategories((current) => {
      const next = current.filter((category) => category.id !== activeId);
      setActiveId(next[0]?.id || "");
      return next;
    });
  };

  const addItem = () => {
    const item = {
      id: createMenuId("item"),
      name: "New menu item",
      description: "",
      tag: "",
      price: "",
      available: true,
    };
    updateActiveCategory("items", [...(activeCategory?.items || []), item]);
  };

  const updateItem = (itemId, field, value) => {
    updateActiveCategory(
      "items",
      (activeCategory?.items || []).map((item) =>
        item.id === itemId ? { ...item, [field]: value } : item,
      ),
    );
  };

  const deleteItem = (itemId) => {
    updateActiveCategory(
      "items",
      (activeCategory?.items || []).filter((item) => item.id !== itemId),
    );
  };

  const loadDefaults = () => {
    const nextCategories = withMenuIds(defaultMenu);
    setCategories(nextCategories);
    setActiveId(nextCategories[0]?.id || "");
    setStatus({ type: "info", text: "Website default menu loaded. Save changes to publish it." });
  };

  const saveChanges = async () => {
    const cleaned = cleanMenuForSave(categories);
    if (!cleaned.length) {
      setStatus({ type: "error", text: "Add at least one named section before saving." });
      return;
    }

    setSaving(true);
    setStatus({ type: "loading", text: "Saving menu changes…" });
    try {
      const data = await saveManagedMenu(cleaned);
      const nextCategories = withMenuIds(data.categories);
      setCategories(nextCategories);
      setActiveId((current) => nextCategories.find((category) => category.id === current)?.id || nextCategories[0]?.id || "");
      setStatus({ type: "success", text: "Menu saved. The public menu now uses these changes." });
    } catch (error) {
      setStatus({ type: "error", text: error.message || "Could not save menu changes." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <main data-testid="menu-admin-page" className="min-h-screen bg-[#f9f6f0] text-[#2c3528]">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-12 md:py-16">
        <a
          href="/"
          data-testid="admin-back-link"
          className="inline-flex items-center gap-2 text-sm tracking-[0.16em] uppercase text-[#5b7053] hover:text-[#3f4f39] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.7} />
          Back to website
        </a>

        <div className="mt-8 grid lg:grid-cols-12 gap-8 lg:gap-12 items-end">
          <div className="lg:col-span-7">
            <p data-testid="menu-admin-overline" className="overline text-[#d98a6c]">Menu Management</p>
            <h1 data-testid="menu-admin-title" className="font-serif-display mt-4 text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Keep the taverna menu fresh.
            </h1>
          </div>
          <div className="lg:col-span-5 flex flex-wrap lg:justify-end gap-3">
            <button
              type="button"
              data-testid="admin-load-defaults-button"
              onClick={loadDefaults}
              className="lift inline-flex items-center gap-2 border border-[#2c3528]/20 px-5 py-3 text-sm tracking-[0.16em] uppercase hover:bg-[#2c3528] hover:text-[#f9f6f0]"
            >
              <RotateCcw className="h-4 w-4" strokeWidth={1.7} />
              Load defaults
            </button>
            <button
              type="button"
              data-testid="admin-save-button"
              onClick={saveChanges}
              disabled={saving}
              className="lift inline-flex items-center gap-2 bg-[#5b7053] text-[#f9f6f0] px-6 py-3 text-sm tracking-[0.16em] uppercase hover:bg-[#3f4f39] disabled:opacity-60"
            >
              <Save className="h-4 w-4" strokeWidth={1.7} />
              {saving ? "Saving" : "Save changes"}
            </button>
          </div>
        </div>

        <div
          data-testid="menu-admin-status"
          className={`mt-8 border px-5 py-4 text-sm ${statusClasses[status.type] || statusClasses.info}`}
        >
          {status.text}
        </div>

        <div className="mt-8 grid lg:grid-cols-[320px_minmax(0,1fr)] gap-8">
          <CategoryList categories={categories} activeId={activeId} onSelect={setActiveId} onAdd={addCategory} />

          <section data-testid="menu-admin-editor" className="bg-[#f0ebe1] border border-[#2c3528]/15 p-5 md:p-8">
            {activeCategory ? (
              <>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
                  <div className="flex-1 grid gap-4">
                    <label className="block">
                      <span className="text-xs tracking-[0.16em] uppercase text-[#2c3528]/65">Section name</span>
                      <input
                        data-testid="admin-category-name-input"
                        value={activeCategory.name}
                        onChange={(event) => updateActiveCategory("name", event.target.value)}
                        className="mt-2 w-full bg-white/70 border border-[#2c3528]/20 px-4 py-3 text-[#2c3528] outline-none focus:border-[#5b7053]"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs tracking-[0.16em] uppercase text-[#2c3528]/65">Section note</span>
                      <input
                        data-testid="admin-category-blurb-input"
                        value={activeCategory.blurb}
                        onChange={(event) => updateActiveCategory("blurb", event.target.value)}
                        className="mt-2 w-full bg-white/70 border border-[#2c3528]/20 px-4 py-3 text-[#2c3528] outline-none focus:border-[#5b7053]"
                      />
                    </label>
                  </div>
                  <button
                    type="button"
                    data-testid="admin-delete-category-button"
                    onClick={deleteActiveCategory}
                    className="lift inline-flex items-center justify-center gap-2 border border-[#8f3d2f]/35 text-[#8f3d2f] px-5 py-3 text-sm tracking-[0.16em] uppercase hover:bg-[#8f3d2f] hover:text-[#f9f6f0]"
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.7} />
                    Delete section
                  </button>
                </div>

                <div className="mt-8 flex items-center justify-between gap-4 border-t border-[#2c3528]/15 pt-8">
                  <h2 data-testid="admin-items-title" className="font-serif-display text-3xl md:text-4xl">
                    Menu items
                  </h2>
                  <button
                    type="button"
                    data-testid="admin-add-item-button"
                    onClick={addItem}
                    className="lift inline-flex items-center gap-2 bg-[#2c3528] text-[#f9f6f0] px-5 py-3 text-sm tracking-[0.16em] uppercase hover:bg-[#3f4f39]"
                  >
                    <Plus className="h-4 w-4" strokeWidth={1.7} />
                    Add item
                  </button>
                </div>

                <div className="mt-6 space-y-4">
                  {(activeCategory.items || []).map((item, index) => (
                    <ItemEditor
                      key={item.id}
                      item={item}
                      index={index}
                      onChange={(field, value) => updateItem(item.id, field, value)}
                      onDelete={() => deleteItem(item.id)}
                    />
                  ))}
                </div>
              </>
            ) : (
              <div data-testid="admin-empty-state" className="py-20 text-center">
                <p className="font-serif-display text-3xl">No menu section selected.</p>
                <button
                  type="button"
                  data-testid="admin-empty-add-category-button"
                  onClick={addCategory}
                  className="lift mt-6 inline-flex items-center gap-2 bg-[#5b7053] text-[#f9f6f0] px-6 py-3 text-sm tracking-[0.16em] uppercase hover:bg-[#3f4f39]"
                >
                  <Plus className="h-4 w-4" strokeWidth={1.7} />
                  Add first section
                </button>
              </div>
            )}
          </section>
        </div>
      </section>
    </main>
  );
}